export default function ComparisonSection() {
  const comparisons = [
    {
      factor: 'Timeline',
      traditional: '6-12 months average',
      trex: '7-14 days to closing'
    },
    {
      factor: 'Tenant Situation',
      traditional: 'Usually requires vacant property',
      trex: 'Buy with tenants in place'
    },
    {
      factor: 'Showings',
      traditional: 'Multiple showings, open houses',
      trex: 'One inspection, minimal disruption'
    },
    {
      factor: 'Repairs',
      traditional: 'Buyers demand repairs or credits',
      trex: 'Buy as-is, no repairs needed'
    },
    {
      factor: 'Commissions',
      traditional: '5-6% to real estate agents',
      trex: 'Zero commissions or fees'
    },
    {
      factor: 'Closing Costs',
      traditional: 'Seller pays 2-3% closing costs',
      trex: 'We cover all closing costs'
    },
    {
      factor: 'Financing Risk',
      traditional: 'Deals fall through if financing fails',
      trex: 'All-cash, no financing contingency'
    },
    {
      factor: 'Rental Income',
      traditional: 'Lost during vacancy period',
      trex: 'Keep collecting until closing'
    },
    {
      factor: 'Tenant Cooperation',
      traditional: 'Required for showings, inspections',
      trex: 'Minimal tenant involvement needed'
    },
    {
      factor: 'Legal Protection',
      traditional: 'Varies by agent/buyer',
      trex: '100% title company closings'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Traditional Sale vs. Selling to T-Rex Real Estate
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            See the clear difference between selling tenant occupied property traditionally versus selling to us.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-gray-900 to-gray-800 text-white">
                  <th className="px-6 py-4 text-left text-lg font-bold">Factor</th>
                  <th className="px-6 py-4 text-left text-lg font-bold">Traditional Sale</th>
                  <th className="px-6 py-4 text-left text-lg font-bold bg-teal-600">T-Rex Real Estate</th>
                </tr>
              </thead>
              <tbody>
                {comparisons.map((item, index) => (
                  <tr 
                    key={index}
                    className={`border-b border-gray-200 hover:bg-gray-50 transition-colors ${
                      index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                    }`}
                  >
                    <td className="px-6 py-4 font-bold text-gray-900">{item.factor}</td>
                    <td className="px-6 py-4 text-gray-600">
                      <div className="flex items-center">
                        <i className="ri-close-circle-fill text-red-500 mr-2 flex-shrink-0"></i>
                        <span>{item.traditional}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-900 bg-teal-50">
                      <div className="flex items-center">
                        <i className="ri-check-double-fill text-teal-600 mr-2 flex-shrink-0"></i>
                        <span className="font-semibold">{item.trex}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-8">
          <div className="bg-red-50 border-l-4 border-red-500 p-8 rounded-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <i className="ri-time-fill text-red-500 mr-3"></i>
              Traditional Sale Costs
            </h3>
            <div className="space-y-3 text-gray-700">
              <div className="flex justify-between items-center">
                <span>Real Estate Commission (6%):</span>
                <span className="font-bold">$18,000</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Closing Costs (2.5%):</span>
                <span className="font-bold">$7,500</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Repairs & Updates:</span>
                <span className="font-bold">$15,000</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Lost Rent (6 months):</span>
                <span className="font-bold">$12,000</span>
              </div>
              <div className="border-t-2 border-red-300 pt-3 flex justify-between items-center text-xl">
                <span className="font-bold">Total Cost:</span>
                <span className="font-bold text-red-600">$52,500</span>
              </div>
            </div>
          </div>

          <div className="bg-teal-50 border-l-4 border-teal-600 p-8 rounded-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <i className="ri-money-dollar-circle-fill text-teal-600 mr-3"></i>
              Selling to T-Rex
            </h3>
            <div className="space-y-3 text-gray-700">
              <div className="flex justify-between items-center">
                <span>Real Estate Commission:</span>
                <span className="font-bold">$0</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Closing Costs:</span>
                <span className="font-bold">$0</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Repairs & Updates:</span>
                <span className="font-bold">$0</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Lost Rent:</span>
                <span className="font-bold">$0</span>
              </div>
              <div className="border-t-2 border-teal-300 pt-3 flex justify-between items-center text-xl">
                <span className="font-bold">Total Cost:</span>
                <span className="font-bold text-teal-600">$0</span>
              </div>
            </div>
            <div className="mt-6 bg-teal-600 text-white p-4 rounded-lg text-center">
              <div className="text-3xl font-bold mb-1">Save $52,500+</div>
              <div className="text-teal-100">On a $300,000 property</div>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <p className="text-xl text-gray-600 mb-8">
            The choice is clear. Save time, money, and stress by selling your tenant occupied property to us.
          </p>
          <a 
            href="tel:+1-915-206-5814"
            className="inline-flex items-center bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg text-lg font-bold transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-phone-fill mr-2"></i>
            Call (915) 206-5814 for Your Offer
          </a>
        </div>
      </div>
    </section>
  );
}